import React, { useContext, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../contexts/AppContext";
import MeasuresContext from "../../contexts/Measures";
import FiltersContext from "../../contexts/Filters";
import { LoadingComponent, useExecutionDataView } from "@gooddata/sdk-ui";
import { Modal } from "antd";
import { utils, writeFileXLSX } from "xlsx";
import { mode, weigthedAverage } from "../../utils/calculationUtils";
import { operations } from "./CarSelectorConfigModal";
import { useCarSelector } from "../../contexts/CarSelectorProvider";

// array of columns key used for sorting
const primaryColumnKeys = ["Version", "List Price", "Average"];

export default function VehicleExport({ seriesBy, slicesBy, filters, kpi, onDownloadCallback }) {
    const { getCustlabelByCustId } = useAppContext();
    const { customFields } = useCarSelector();
    const { selectedAdjDeposit } = useContext(MeasuresContext);
    const { getFirstFilterValue } = useContext(FiltersContext);
    const { result } = useExecutionDataView({
        execution: {
            seriesBy,
            slicesBy,
            filters,
        },
    });
    const [data, setData] = useState([]);
    const fileName = `Version-rental-prices_${getFirstFilterValue("filterCountryId")}-${getFirstFilterValue("filterChannel1")}-${getFirstFilterValue("filterDuration")}.xlsx`;

    const filteredFlat = useMemo(() => {
        const slices = result?.data().slices().toArray();
        const flat = slices?.map((slice) => {
            const dataPoints = slice.dataPoints();

            return Object.assign({
                Version: slice.sliceTitles()[0],
                "List Price": Math.round(Number(slice.dataPoints()[1].rawValue)),
                "Adj. Deposit": slice.dataPoints()[2].rawValue,
                CustId: slice.sliceTitles()[1],
                Value: dataPoints[kpi?.index]?.rawValue !== null ? kpi.roundValue(dataPoints[kpi?.index]?.rawValue) : null,
                versionCount: Number(dataPoints[30].rawValue),
            });
        });
        if (selectedAdjDeposit !== "Total Payable by month") {
            return flat?.filter((item) => item["Adj. Deposit"] !== null);
        }
        return flat;
    }, [selectedAdjDeposit, result]);

    useEffect(() => {
        if (filteredFlat?.length > 0) {
            // @ts-ignore
            const custIdKeys = [...new Set(filteredFlat.map(({ CustId }) => CustId))].filter((custId) => isNaN(custId) && custId !== null);
            let vehicleOffersObj = filteredFlat.reduce((acc, nextItem) => {
                const vehicleInfo = {
                    Version: nextItem?.Version,
                };
                if (nextItem?.Version in acc && nextItem.CustId in acc[nextItem?.Version]) {
                    acc[nextItem?.Version][nextItem.CustId].values.push(nextItem.Value);
                    acc[nextItem?.Version][nextItem.CustId].versionCount.push(nextItem.versionCount);
                } else {
                    acc[nextItem.Version] = {
                        [nextItem.CustId]: {
                            values: [nextItem.Value],
                            versionCount: [nextItem.versionCount],
                        },
                        ...vehicleInfo,
                        ...acc[nextItem?.Version],
                    };
                }
                acc[nextItem?.Version] = {
                    ...acc[nextItem?.Version],
                    listPrices: "listPrices" in acc[nextItem?.Version] ? [...acc[nextItem?.Version]["listPrices"], nextItem["List Price"]] : [nextItem["List Price"]],
                };
                return acc;
            }, {});

            // Calculate the weighted average
            Object.keys(vehicleOffersObj).forEach((key) => {
                custIdKeys.forEach((custId) => {
                    const newCusIdVal = custId in vehicleOffersObj[key] && vehicleOffersObj[key][custId]?.values.filter((val) => val !== null).length > 0 ? weigthedAverage(vehicleOffersObj[key][custId]?.values, vehicleOffersObj[key][custId]?.versionCount) : null;
                    vehicleOffersObj[key][custId] = newCusIdVal !== null ? (kpi.index === 24 ? Number(newCusIdVal.toFixed(2)) : kpi.roundValue(newCusIdVal)) : null;
                });
            });

            const dataSource = Object.values(vehicleOffersObj).map((item) => {
                const custValues = Object.keys(item)
                    .filter((key) => custIdKeys.includes(key) && item[key] > 0)
                    .map((key) => item[key]);
                let rentalSum = custValues.reduce((acc, currentVal) => acc + currentVal, 0);
                let Average = rentalSum > 0 ? (kpi.index === 24 ? Number((rentalSum / custValues.length).toFixed(2)) : Math.round(rentalSum / custValues.length)) : null;
                let ModListPrice = item.listPrices?.length > 0 ? Math.round(Number(mode(item.listPrices))) || null : null;
                const customFieldsObj = customFields.reduce((acc, field) => {
                    const fieldValue = operations.calculate(
                        field.players.map((player) => item[player] || null),
                        field.operation,
                        (value) => (kpi.index === 24 ? Number(value.toFixed(2)) : Math.round(value)),
                    );
                    return {
                        ...acc,
                        [field.fieldName]: fieldValue,
                    };
                }, {});
                // Convert custId keys to their corresponding labels
                Object.keys(item)
                    .sort((a, b) => a.localeCompare(b))
                    .reduce((acc, key) => {
                        if (custIdKeys.includes(key)) {
                            const custLabel = getCustlabelByCustId(key);
                            if (key !== custLabel) {
                                item[custLabel] = item[key];
                                delete item[key];
                            }
                        }

                        return acc;
                    }, {});

                delete item["listPrices"];
                return { Version: item?.Version, "List Price": ModListPrice, Average, ...customFieldsObj, ...item };
            });

            setData(dataSource);
        }
    }, [filteredFlat]);

    useEffect(() => {
        if (data.length > 0) {
            const ws = utils.json_to_sheet(data);
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws, "Data");
            writeFileXLSX(wb, fileName);
            onDownloadCallback();
        }
    }, [data]);

    return (
        <Modal open={data?.length <= 0} footer={null} width={800}>
            <div className="flex flex-col items-center justify-center gap-2 h-64">
                <div className="text-lg font-semibold text-gray-500">Generating your speadsheet</div>
                <span className="font-medium text-gray-500">Please, wait a moment</span>
                <div>
                    <LoadingComponent />
                </div>
            </div>
        </Modal>
    );
}
